/**
 * Converte URL relativa (/static/...) em URL absoluta baseada no host da API.
 * @param {string} url - URL retornada pela API (pode ser relativa ou absoluta)
 * @returns {string} URL absoluta para uso em src/href
 */
export function toAbsoluteMediaUrl(url, opts = {}) {
  if (!url || typeof url !== "string") return url || "";
  const trimmed = url.trim();

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://"))
    return trimmed;
  const base = getApiBase().replace(/\/+$/, "");
  return trimmed.startsWith("/") ? base + trimmed : base + "/" + trimmed;
}

const isLocalApiHost = (host) =>
  /^(localhost|127\.|10\.|192\.168\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/i.test(
    host || "",
  );

const normalizeApiBase = (value) => {
  const raw = String(value || "").trim().replace(/\/+$/, "");
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    if (parsed.protocol === "http:" && !isLocalApiHost(parsed.hostname)) {
      parsed.protocol = "https:";
      return parsed.toString().replace(/\/+$/, "");
    }
  } catch {}
  return raw;
};

export const getApiBase = () => {
  try {
    const explicit = (window.AGENTS_WIDGET_API_BASE || "").trim();
    if (explicit) return normalizeApiBase(explicit);

    const scriptTag = document.querySelector(
      'script[data-public-key][data-api-base]'
    );
    if (scriptTag) {
      const fromTag = (scriptTag.getAttribute("data-api-base") || "").trim();
      if (fromTag) return normalizeApiBase(fromTag);
    }

    const envRaw = (window.AGENTS_WIDGET_API_ENV || "")
      .toString()
      .toLowerCase()
      .trim();
    if (envRaw === "dev") return "http://localhost:8000";
    if (envRaw === "staging") return "https://stg-agents-ai-api.inovationai.ai";
    if (envRaw === "prod" || envRaw === "production")
      return "https://agents-ai-api.inovation.com.br";

    let host =
      typeof location !== "undefined" && location.hostname
        ? location.hostname
        : "";

    if (!host && typeof document !== "undefined" && document.referrer) {
      try { host = new URL(document.referrer).hostname; } catch {}
    }

    if (
      /^(localhost|127\.|10\.|192\.168\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/i.test(
        host
      )
    ) {
      return normalizeApiBase("http://localhost:8000");
    }
    if (/(^|\.)stg(-|\.)|staging/i.test(host)) {
      return normalizeApiBase("https://stg-agents-ai-api.inovationai.ai");
    }
    return normalizeApiBase("https://agents-ai-api.inovation.com.br");
  } catch {
    return "https://agents-ai-api.inovation.com.br";
  }
};

export async function createWidgetSession(payload) {
  const res = await fetch(`${getApiBase()}/widget/create-session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("create-session failed");
  return res.json();
}

export async function sendWidgetMessage(payload) {
  const res = await fetch(`${getApiBase()}/widget/message`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("send-message failed");
  return res.json();
}

/** Widget Agent Live (realtime S2S) — public_key + visitor_token auth. */
export async function startWidgetLive(payload) {
  const res = await fetch(`${getApiBase()}/widget-live/sessions/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = new Error("widget-live-start failed");
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function widgetLiveWebrtc(callId, payload) {
  const res = await fetch(
    `${getApiBase()}/widget-live/sessions/${encodeURIComponent(callId)}/webrtc`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) throw new Error("widget-live-webrtc failed");
  return res.json();
}

export async function postWidgetLiveTurn(payload) {
  const res = await fetch(`${getApiBase()}/widget-live/sessions/turns`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("widget-live-turn failed");
  return res.json();
}

export async function endWidgetLive(payload) {
  const res = await fetch(`${getApiBase()}/widget-live/sessions/end`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("widget-live-end failed");
  return res.json();
}

export async function uploadWidgetMedia(sessionId, publicKey, visitorToken, file) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("session_id", String(sessionId));
  fd.append("public_key", publicKey);
  fd.append("visitor_token", visitorToken);
  const res = await fetch(`${getApiBase()}/widget/upload`, {
    method: "POST",
    body: fd,
  });
  if (!res.ok) throw new Error("upload failed");
  return res.json();
}

export async function sendReaction(sessionId, publicKey, visitorToken, messageId, emoji) {
  const res = await fetch(`${getApiBase()}/widget/reaction`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_id: sessionId,
      public_key: publicKey,
      visitor_token: visitorToken,
      message_id: messageId,
      emoji,
    }),
  });
  if (!res.ok) throw new Error("send-reaction failed");
  return res.json();
}

/**
 * Busca mensagens da sessão do widget (para histórico e polling).
 * @param {number} sessionId
 * @param {string} publicKey
 * @param {string} visitorToken
 * @param {Object} [opts] - { after, before, limit, order }
 * @returns {Promise<{ messages: Array, session_status?: string|null }>}
 */
export async function getWidgetMessages(sessionId, publicKey, visitorToken, opts = {}) {
  const { after, afterId, before, limit, order, timeoutMs = 8000 } = opts;
  const params = new URLSearchParams({
    session_id: String(sessionId),
    public_key: publicKey,
    visitor_token: visitorToken,
  });
  if (afterId != null && afterId !== undefined) params.set("after_id", String(afterId));
  if (after) params.set("after", String(after));
  if (before) params.set("before", String(before));
  if (limit != null && limit !== undefined)
    params.set("limit", String(parseInt(String(limit), 10)));
  if (order) params.set("order", String(order).toLowerCase());
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => {
        try { controller.abort(); } catch {}
      }, timeoutMs)
    : null;
  let res;
  try {
    res = await fetch(
      `${getApiBase()}/widget/messages?${params.toString()}`,
      { method: "GET", signal: controller ? controller.signal : undefined }
    );
  } finally {
    if (timeout) clearTimeout(timeout);
  }
  if (!res.ok) {
    const err = new Error("get-messages failed");
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  const messages = Array.isArray(data?.messages)
    ? data.messages
    : Array.isArray(data)
      ? data
      : [];
  return {
    messages,
    session_status: data?.session_status ?? null,
  };
}

/**
 * Abre WebSocket real-time para receber mensagens push do servidor.
 * Reconecta automaticamente com backoff exponencial.
 * @param {number} sessionId
 * @param {string} publicKey
 * @param {string} visitorToken
 * @param {function|object} callbacks - function(msg) for backward compat, or { onMessage, onReaction, onStatusUpdate, onTyping }
 * @returns {{ close: function, send: function }} controle para fechar/enviar
 */
export function connectWidgetWebSocket(sessionId, publicKey, visitorToken, callbacks) {
  const cbs = typeof callbacks === "function" ? { onMessage: callbacks } : (callbacks || {});
  let ws = null;
  let alive = true;
  let retries = 0;
  const MAX_RETRIES = 10;
  let pingInterval = null;

  function getWsUrl() {
    const httpBase = getApiBase();
    const wsBase = httpBase
      .replace(/^https:/, "wss:")
      .replace(/^http:/, "ws:");
    return `${wsBase}/widget/ws/${sessionId}?public_key=${encodeURIComponent(publicKey)}&visitor_token=${encodeURIComponent(visitorToken)}`;
  }

  function connect() {
    if (!alive) return;
    try {
      ws = new WebSocket(getWsUrl());
    } catch {
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      retries = 0;
      pingInterval = setInterval(() => {
        try { if (ws.readyState === 1) ws.send("ping"); } catch {}
      }, 25000);
    };

    ws.onmessage = (evt) => {
      if (evt.data === "pong") return;
      try {
        const data = JSON.parse(evt.data);
        switch (data.type) {
          case "new_message":
            if (data.message && cbs.onMessage) cbs.onMessage(data.message);
            break;
          case "message_reaction":
            if (cbs.onReaction) cbs.onReaction(data);
            break;
          case "message_status_update":
            if (cbs.onStatusUpdate) cbs.onStatusUpdate(data);
            break;
          case "typing_indicator":
          case "typing":
            if (cbs.onTyping) cbs.onTyping(data);
            break;
        }
      } catch {}
    };

    ws.onclose = () => {
      clearInterval(pingInterval);
      scheduleReconnect();
    };

    ws.onerror = () => {
      try { ws.close(); } catch {}
    };
  }

  function scheduleReconnect() {
    if (!alive || retries >= MAX_RETRIES) return;
    const delay = Math.min(1000 * Math.pow(2, retries), 30000);
    retries++;
    setTimeout(connect, delay);
  }

  connect();

  return {
    close() {
      alive = false;
      clearInterval(pingInterval);
      try { if (ws) ws.close(); } catch {}
    },
    send(data) {
      try {
        if (ws && ws.readyState === 1) {
          ws.send(typeof data === "string" ? data : JSON.stringify(data));
        }
      } catch {}
    },
  };
}
